import api from './api';

/**
 * Feed Service - Handles all feed-related API calls
 */
const feedService = {
  /**
   * Get the feed releases from followed artists
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of releases to return
   * @param {number} params.page - Page number
   * @returns {Promise} Resolves to feed releases
   */
  getFeed: async (params = {}) => {
    const defaultParams = {
      limit: 10,
      page: 0,
    };
    const queryParams = { ...defaultParams, ...params };
    const response = await api.get('api/mobile/feed', { params: queryParams });
    return response.data;
  },

  /**
   * Get a specific feed item by ID
   * @param {string} id - Feed item ID
   * @returns {Promise} Resolves to feed item details
   */
  getFeedItem: async (id) => {
    const response = await api.get(`api/mobile/feed/${id}`);
    return response.data;
  },

  /**
   * Get featured releases (PRO users can see and feature releases)
   * @returns {Promise} Resolves to featured releases
   */
  getFeaturedReleases: async () => {
    const response = await api.get('api/featured-releases');
    return response.data;
  },

  /**
   * Feature a release (costs credits)
   * @param {string} releaseId - Release ID to feature
   * @returns {Promise} Resolves to feature request result
   */
  featureRelease: async (releaseId) => {
    const response = await api.put(`api/distribution/${releaseId}/feature`);
    return response.data;
  },

  /**
   * Request a release to be featured (free request, reviewed by admin)
   * @param {string} releaseId - Release ID to request feature
   * @returns {Promise} Resolves to request result
   */
  requestFeature: async (releaseId) => {
    const response = await api.put(`api/distribution/${releaseId}/feature`);
    return response.data;
  },

  /**
   * Get comments for a release
   * @param {string} releaseId - Release ID
   * @returns {Promise} Resolves to comments list
   */
  getComments: async (releaseId) => {
    const response = await api.get(`api/distribution/${releaseId}/comments`);
    return response.data;
  },

  /**
   * Add a comment/feedback to a release
   * @param {string} releaseId - Release ID
   * @param {Object} commentData - Comment data
   * @param {string} commentData.strength - Positive feedback type
   * @param {string} commentData.areas_of_improvement - Areas for improvement
   * @param {string} commentData.author - Artist ID commenting as
   * @returns {Promise} Resolves to comment result
   */
  addComment: async (releaseId, commentData) => {
    const response = await api.post(`api/distribution/${releaseId}/comments`, commentData);
    return response.data;
  },

  /**
   * Get artists the current user is following
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.limit - Number of items per page (default: 25)
   * @returns {Promise} Resolves to list of followed artists
   */
  getFollowedArtists: async (params = {}) => {
    const defaultParams = {
      page: 0,
      limit: 25,
    };
    const queryParams = { ...defaultParams, ...params };
    const response = await api.get('api/user/following', { params: queryParams });
    return response.data;
  },

  /**
   * Get latest releases from followed artists
   * @param {Object} params - Query parameters
   * @returns {Promise} Resolves to releases from followed artists
   */
  getFollowedArtistsReleases: async (params = {}) => {
    const defaultParams = {
      limit: 20,
      page: 0,
      status: 3, // Published status
    };
    const queryParams = { ...defaultParams, ...params };
    const response = await api.get('api/followed-releases', { params: queryParams });
    return response.data;
  },

  /**
   * Get latest published releases for the Discover tab
   * @param {Object} params - Query parameters
   * @returns {Promise} Resolves to latest releases
   */
  getDiscoverReleases: async (params = {}) => {
    const defaultParams = {
      limit: 20,
      page: 0,
    };
    const queryParams = { ...defaultParams, ...params };
    const response = await api.get('api/releases', { params: queryParams });
    return response.data;
  },

  /**
   * Get user credits
   * @returns {Promise} Resolves to user credits
   */
  getCredits: async () => {
    const response = await api.get('api/user/credits');
    return response.data;
  },

  /**
   * Get feedback received on user's releases
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.limit - Number of items per page
   * @returns {Promise} Resolves to list of received feedback with total, results, and items
   */
  getReceivedFeedback: async (params = {}) => {
    const defaultParams = {
      page: 0,
      limit: 25,
    };
    const queryParams = { ...defaultParams, ...params };
    const response = await api.get('api/distributions-comments', { params: queryParams });
    return response.data;
  },
};

export default feedService;
